import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getArticlesPage, getTopicTagCounts } from '@/services/unifiedNewsService';
import { useLocale } from '@/i18n/useLocale';
import { TRANSLATION_CODE } from '@/i18n/config';
import { NewsArticle } from '@/types/news';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuth } from '@/context/AuthContext';
import { CATEGORY_TAGS } from '@/lib/news/categoryTags';

// Define filter state type for better type safety
export type SortOption = 'latest' | 'trending' | 'discussed';
export type DateRangeOption = 'all' | 'today' | '3days' | 'week' | '2weeks' | 'month' | '3months';

export interface FilterState {
  search: string;
  categories: string[];
  sourceIds: string[];
  sort: SortOption;
  dateRange: DateRangeOption;
  page: number;
  limit: number;
}

interface NewsContextType {
  articles: NewsArticle[];
  featuredArticles: NewsArticle[];
  allCachedArticles: NewsArticle[];
  availableSources: { id: string; name: string }[];
  categoryCounts: { value: string; label: string; count: number }[];
  isLoading: boolean;
  error: Error | null;
  filters: FilterState;
  setFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
  resetFilters: () => void;
  loadMore: () => void;
  hasMore: boolean;
  refetch: () => void;
  triggerSync: () => Promise<void>;
  syncStatus: {
    lastSync?: Date;
    articlesCount: number;
    needsSync: boolean;
  };
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    totalArticles: number;
  };
}

const PAGE_SIZE = 24;
/** Shared with the home route loader so SSR primes the same cache entry. */
export const FEED_PAGE_SIZE = PAGE_SIZE;

const NewsContext = createContext<NewsContextType | null>(null);

// Initial filter state
const initialFilterState: FilterState = {
  search: '',
  categories: [],
  sourceIds: [],
  sort: 'latest',
  dateRange: 'all',
  page: 1,
  limit: 20,
};

export const NewsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  // On the Norwegian and Chinese editions only fully translated stories are listed.
  const locale = useLocale();
  const translatedIn = TRANSLATION_CODE[locale];
  
  // Debounce search to avoid excessive filtering
  const debouncedSearch = useDebounce(filters.search, 300);

  // Selected topic tags, normalised back to their canonical labels. These are
  // sent to the database so filtering covers the whole archive, not just the
  // pages already loaded in the browser.
  const activeTags = useMemo(() => {
    const selected = filters.categories.map((c) => c.toLowerCase());
    if (selected.length === 0 || selected.includes('all')) return [] as string[];
    return CATEGORY_TAGS.filter(
      (tag) => tag !== 'All' && selected.includes(tag.toLowerCase())
    ) as unknown as string[];
  }, [filters.categories]);

  // Fetch sync status
  const { data: syncStatus } = useQuery({
    queryKey: ['sync-status'],
    queryFn: async () => (await import('@/services/adminNewsService')).getSyncStatus(),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000
  });

  // Feed: lean columns + keyset pagination. Each scroll appends a page instead
  // of re-downloading the whole list.
  const {
    data: articlesData,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['articles', 'feed', debouncedSearch, activeTags.join(','), translatedIn ?? 'en'],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      getArticlesPage({
        search: debouncedSearch || undefined,
        cursor: pageParam,
        limit: PAGE_SIZE,
        tags: activeTags,
        translatedIn,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });



  // Manual sync trigger
  const triggerSync = async () => {
    try {
      toast.info("Syncing articles...", {
        description: "Fetching the latest news.",
        id: "manual-sync"
      });

      const { triggerFreshSync } = await import('@/services/adminNewsService');
      const result = await triggerFreshSync();
      
      if (result.success) {
        toast.success("Articles synced", {
          description: result.message,
          id: "manual-sync"
        });
        
        // Refetch after successful sync
        setTimeout(() => refetch(), 3000);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('❌ Manual sync failed:', error);
      toast.error("Sync failed", {
        description: "Failed to sync articles.",
        id: "manual-sync"
      });
    }
  };

  // Get all articles for caching
  const allCachedArticles = useMemo(() => {
    const pages = articlesData?.pages || [];
    const seen = new Set<string>();
    const out: NewsArticle[] = [];
    for (const page of pages) {
      for (const a of page.articles) {
        if (seen.has(a.id)) continue;
        seen.add(a.id);
        out.push(a);
      }
    }
    return out;
  }, [articlesData]);

  // Derive unique available sources from cached articles
  const availableSources = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    allCachedArticles.forEach(a => {
      if (a.source?.id && a.source?.name && !map.has(a.source.id)) {
        map.set(a.source.id, { id: a.source.id, name: a.source.name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allCachedArticles]);

  // Tag counts across the WHOLE published archive (stable while scrolling)
  const { data: tagCountMap } = useQuery({
    queryKey: ['articles', 'topic-tag-counts'],
    queryFn: getTopicTagCounts,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });

  const categoryCounts = useMemo(() => {
    const counts = tagCountMap || {};
    return CATEGORY_TAGS.map((tag) => ({
      value: tag.toLowerCase(),
      label: tag,
      count: tag === 'All' ? (syncStatus?.articlesCount ?? allCachedArticles.length) : (counts[tag] ?? 0),
    }));
  }, [tagCountMap, allCachedArticles.length, syncStatus?.articlesCount]);

  // Tag filtering happens server-side; remaining client-side filters below.
  const processedArticles = useMemo(() => {
    let list = allCachedArticles;


    if (filters.sourceIds.length > 0) {
      const set = new Set(filters.sourceIds);
      list = list.filter(a => a.source?.id && set.has(a.source.id));
    }

    if (filters.dateRange !== 'all') {
      const now = Date.now();
      const dayMs = 24 * 60 * 60 * 1000;
      const cutoffMap: Record<string, number> = {
        today: dayMs,
        '3days': 3 * dayMs,
        week: 7 * dayMs,
        '2weeks': 14 * dayMs,
        month: 30 * dayMs,
        '3months': 90 * dayMs,
      };
      const cutoff = cutoffMap[filters.dateRange];
      if (cutoff) {
        list = list.filter(a => {
          const t = new Date(a.publishedAt).getTime();
          return !isNaN(t) && now - t <= cutoff;
        });
      }
    }

    if (filters.sort === 'trending') {
      list = [...list].sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
    } else if (filters.sort === 'discussed') {
      list = [...list].sort((a, b) => (b.comments_count || 0) - (a.comments_count || 0));
    }
    // 'latest' is the default order from the query

    return list;
  }, [allCachedArticles, filters.categories, filters.sourceIds, filters.dateRange, filters.sort]);

  // Enhanced pagination - show articles based on page with proper infinite scroll
  const paginatedArticles = processedArticles;

  // Set a specific filter value
  const setFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    if (key === 'page') {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
      return;
    }
    setFilters(prev => {
      // If changing anything other than page, reset to page 1
      if (key !== 'page') {
        return { ...prev, [key]: value, page: 1 };
      }
      return { ...prev, [key]: value };
    });
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Reset all filters to initial state
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const resetFilters = useCallback(() => {
    setFilters(initialFilterState);
  }, []);

  // Featured articles are the top 6 most recent
  const featuredArticles = useMemo(() => {
    return allCachedArticles.slice(0, 6);
  }, [allCachedArticles]);
  
  // Show error toast if fetching fails
  useEffect(() => {
    if (error) {
      toast.error("Failed to load articles", {
        description: "There was an error fetching articles. Please try refreshing.",
        action: {
          label: "Retry",
          onClick: () => refetch()
        }
      });
    }
  }, [error, refetch]);

  // Calculate pagination info for infinite scroll
  const totalArticles = processedArticles.length;
  const loadedPages = articlesData?.pages.length || 1;
  const totalPages = hasNextPage ? loadedPages + 1 : loadedPages;

  const contextValue = useMemo(() => ({
    articles: paginatedArticles,
    featuredArticles,
    allCachedArticles,
    availableSources,
    categoryCounts,
    isLoading,
    error: error as Error | null,
    filters,
    setFilter,
    resetFilters,
    loadMore,
    hasMore: !!hasNextPage,
    refetch,
    triggerSync,
    syncStatus: syncStatus || { articlesCount: 0, needsSync: true },
    pagination: {
      page: loadedPages,
      pageSize: PAGE_SIZE,
      totalPages,
      totalArticles,
    },
  }), [
    paginatedArticles,
    featuredArticles,
    allCachedArticles,
    availableSources,
    isLoading,
    error,
    filters,
    setFilter,
    loadedPages,
    resetFilters,
    loadMore,
    hasNextPage,
    refetch,
    triggerSync,
    syncStatus,
    totalPages,
    totalArticles
  ]);

  return (
    <NewsContext.Provider value={contextValue}>
      {children}
    </NewsContext.Provider>
  );
};

export const useNews = () => {
  const context = useContext(NewsContext);
  
  if (context === null) {
    throw new Error('useNews must be used within a NewsProvider');
  }
  return context;
};
